import { useEffect, useRef, useState } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import api from "@/lib/api";
import { addBasemap } from "@/lib/basemap";
import { Loader2, MapPin } from "lucide-react";

const pinIcon = () =>
  L.divIcon({
    className: "",
    html: `<div style="width:18px;height:18px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);background:#1d6cb0;border:2px solid #fff;box-shadow:0 2px 4px rgba(0,0,0,.4)"></div>`,
    iconSize: [18, 18],
    iconAnchor: [9, 18],
  });

export default function CustomerMap({ locations = [], height = 240 }) {
  const mapEl = useRef(null);
  const mapRef = useRef(null);
  const [points, setPoints] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const zips = [...new Set(locations.map((l) => (l.zip || "").trim()).filter((z) => /^\d{5}$/.test(z)))];
    if (zips.length === 0) {
      setLoading(false);
      setPoints([]);
      return;
    }
    setLoading(true);
    Promise.all(zips.map((z) => api.get(`/geo/zip/${z}`).then((r) => r.data).catch(() => null)))
      .then((res) => !cancelled && setPoints(res.filter((p) => p && p.lat != null)))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [locations]);

  useEffect(() => {
    if (!points.length || !mapEl.current) return;
    const dark = document.documentElement.classList.contains("dark");
    if (!mapRef.current) {
      mapRef.current = L.map(mapEl.current, { zoomControl: true, scrollWheelZoom: false });
      addBasemap(mapRef.current, { dark });
    }
    const map = mapRef.current;
    const layer = L.layerGroup().addTo(map);
    const latlngs = [];
    points.forEach((p) => {
      L.marker([p.lat, p.lng], { icon: pinIcon() }).addTo(layer).bindTooltip(`${p.city}, ${p.state} ${p.zip}`);
      latlngs.push([p.lat, p.lng]);
    });
    if (latlngs.length === 1) map.setView(latlngs[0], 9);
    else {
      try { map.fitBounds(latlngs, { padding: [40, 40], maxZoom: 9 }); } catch (_) {}
    }
    const t = setTimeout(() => map.invalidateSize(), 120);
    return () => {
      clearTimeout(t);
      map.removeLayer(layer);
    };
  }, [points]);

  useEffect(
    () => () => {
      if (mapRef.current) { mapRef.current.remove(); mapRef.current = null; }
    },
    [],
  );

  if (!loading && points.length === 0) {
    return (
      <div className="flex items-center gap-2 rounded-lg border border-dashed border-border bg-card px-4 py-6 text-sm text-muted-foreground" data-testid="customer-map-empty">
        <MapPin className="h-4 w-4" /> No mappable location (missing ZIP).
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-lg border border-border bg-card" data-testid="customer-map">
      <div className="relative">
        <div ref={mapEl} style={{ height, width: "100%" }} className="z-0" />
        {loading && (
          <div className="absolute inset-0 z-[500] flex items-center justify-center bg-card/70">
            <Loader2 className="h-5 w-5 animate-spin text-[hsl(var(--primary))]" />
          </div>
        )}
      </div>
    </div>
  );
}
